import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useRequest } from 'src/hooks';
import moment from 'moment';
import { JCdTransactionsContext } from 'src/views/trans/JCdTrans/useJCdTransContext';
import { useNotif } from 'src/hooks';
import endpoints from 'src/lib/endpoints';
import { useSelector } from 'react-redux';

const CustomReportSearchModalContext = React.createContext();

export const useCustomReportSearchModal = () => {
  return useContext(CustomReportSearchModalContext);
};

export const CustomReportSearchModalProvider = ({ children, data }) => {
  const req = useRequest();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [modalFilteredData, setModalFilteredData] = useState([]);
  const [reportDefinition, setReportDefinition] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedItems, setSelectedItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasExplicitlyAssigned, setHasExplicitlyAssigned] = useState(false);

  const context = useContext(JCdTransactionsContext);
  const notif = useNotif();
  const { userToken, base_url } = useSelector(({ auth }) => auth);

  const {
    caption = '',
    component_settings = {},
    disabled = false,
    isEditableInViewMode = true
  } = data;
  // Use key directly since field property might not be properly passed
  const fieldKey = data?.key || data?.field || data?.fld || 'customRepInTransaction';

  // Memoize fieldKey to ensure stability
  const stableFieldKey = useMemo(() => fieldKey, [fieldKey]);

  const {
    buttonLabel = 'Search Items',
    buttonDisabled = false,
    buttonColor = 'primary',
    buttonVariant = 'contained',
    ixRep,
    filter,
    require_payload = false,
    employee_sub_type,
    item_sub_type,
    allow_unassign = false
  } = component_settings;

  const transType = context?.meta?.transType;
  const isViewMode = transType === 'view';
  const disableActionButton =
    buttonDisabled ||
    disabled ||
    loading ||
    (isViewMode && !isEditableInViewMode);

  const persistedReportData = useMemo(
    () => context?.transactionsData?.kvs?.[stableFieldKey]?.value,
    [context?.transactionsData?.kvs, stableFieldKey]
  );

  // Filter report data in view mode to show only persisted/saved items
  const filteredReportData = useMemo(() => {
    if (!isViewMode) {
      return reportData;
    }

    // In view mode, just return the persisted data directly
    // This ensures we show the items that were actually selected and saved
    if (!Array.isArray(persistedReportData) || persistedReportData.length === 0) {
      return [];
    }

    return persistedReportData;
  }, [isViewMode, reportData, persistedReportData]);

  const getFieldValue = value => {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.prototype.hasOwnProperty.call(value, 'value')
    ) {
      return value.value;
    }

    return value;
  };

  const normalizeToArray = value => {
    if (typeof value === 'string') {
      const normalized = value.trim();

      if (normalized === '') return [];

      try {
        const parsed = JSON.parse(normalized);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (error) {
        return [];
      }
    }

    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
  };

  const getTransactionFilterValue = key => {
    const [source, field] = key.split('.');
    const {
      transactionsData: {
        journalEntries = [],
        transDetails = {},
        kvs = {}
      } = {}
    } = context;

    let val;
    if (source === 'jv') {
      if (field.startsWith('ixSubAcc')) {
        const firstJE = journalEntries[0];
        const jeValue = getFieldValue(firstJE?.[field]);
        if (jeValue !== undefined && jeValue !== null && jeValue !== '') {
          val = jeValue;
        }
      }

      const detailValue = getFieldValue(transDetails?.[field]);
      if (
        !val &&
        detailValue !== undefined &&
        detailValue !== null &&
        detailValue !== ''
      ) {
        val = detailValue;
      }
    } else if (source === 'kvs') {
      const kvsValue = getFieldValue(kvs?.[field]);
      if (kvsValue !== undefined && kvsValue !== null && kvsValue !== '') {
        val = kvsValue;
      }
    }

    return val;
  };

  const loadReportDefinition = async () => {
    let isMounted = true;
    try {
      const defRes = await req.get(endpoints.setup.customReport.settings(ixRep));
      if (isMounted && defRes.success && defRes.data) {
        setReportDefinition(defRes.data);
      }
    } catch (err) {
      if (isMounted) {
        console.error('❌ Failed to fetch report definition:', err);
      }
    }
    return () => {
      isMounted = false;
    };
  };

  const fetchReportData = async reportDef => {
    if (!reportDef) return;

    setLoading(true);
    try {
      const payload = {
        ixRep,
        dt1: moment().format(),
        dt2: moment().format(),
        ixProdCat: 0,
        ixWH: 0,
        ixProdCatSub: 0,
        ixBrch: 0,
        ixSub: 0,
        ixAcc: 0,
        ixSubType: 0,
        ixSubType2: 0,
        ixSubType3: 0,
        ixSubType4: 0,
        ixSubType5: 0,
        ixSubType6: 0,
        ixSubType7: 0,
        ixSubType8: 0,
        ixSubType9: 0,
        ixSubType10: 0
      };

      let valid = true;

      if (filter && Array.isArray(filter)) {
        filter.forEach(filterItem => {
          const { key, payload_key, type } = filterItem;
          const value = getTransactionFilterValue(key);

          if (
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== 0
          ) {
            if (type === 'string') {
              payload[payload_key] = String(value);
            } else if (type === 'numeric') {
              payload[payload_key] = Number(value);
            } else {
              payload[payload_key] = value;
            }
          } else {
            if (require_payload) {
              valid = false;
              notif.error(`Required filter value missing: ${key}`);
            }
          }
        });
      }

      if (!valid) {
        setLoading(false);
        return;
      }

      const res = await req.post(endpoints.dashboard.report, payload);
      if (res.success && res.data) {
        const data = res.data?.data || res.data || [];
        const dataArray = normalizeToArray(data);
        setReportData(dataArray);
        setModalFilteredData(dataArray);
      } else {
        notif.error('Failed to load report');
      }
    } catch (err) {
      console.error('❌ Failed to load report:', err);
      notif.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async () => {
    if (!ixRep) return;

    setModalOpen(true);
    setLoading(true);
    setSearchTerm('');
    setSelectedItems([]);

    try {
      const defRes = await req.get(endpoints.setup.customReport.settings(ixRep));
      if (defRes.success && defRes.data) {
        setReportDefinition(defRes.data);
        await fetchReportData(defRes.data);
      }
    } catch (err) {
      console.error('❌ Failed to fetch report definition:', err);
      notif.error('Failed to load report definition');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSearchTerm('');
    setSelectedItems([]);
  };

  const handleSearch = event => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);

    if (!term) {
      setModalFilteredData(reportData);
      return;
    }

    const filtered = reportData.filter(item => {
      return Object.values(item).some(value => 
        String(value).toLowerCase().includes(term)
      );
    });

    setModalFilteredData(filtered);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleItemSelection = itemId => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) {
      notif.warning('Please select items to add');
      return;
    }

    // Check if any item ID is undefined
    if (selectedItems.some(id => id === undefined || id === null)) {
      const errorMessage = 'Report data missing item ID. Please include sub_id in report SQL.';
      console.error(errorMessage);
      notif.error(errorMessage);
      return;
    }

    setAdding(true);
    try {
      // Get selected data from filtered report data (modal results)
      const selectedData = modalFilteredData.filter(item =>
        selectedItems.includes(item.sub_id)
      );

      // Get existing persisted data
      const existingData = normalizeToArray(persistedReportData || []);

      // Combine existing data with new selections (avoid duplicates)
      const existingIds = existingData.map(item => item.sub_id);
      const newItems = selectedData.filter(item => !existingIds.includes(item.sub_id));
      const combinedData = [...existingData, ...newItems];

      // Mark that user has explicitly added items
      setHasExplicitlyAssigned(true);

      // Update KVS with combined data
      if (context?.transactionsData?.setKvs) {
        context.transactionsData.setKvs(prev => ({
          ...prev,
          [stableFieldKey]: {
            ...(prev?.[stableFieldKey] ?? {}),
            value: combinedData
          }
        }));
      }

      // Update reportData to show combined data immediately
      setReportData(combinedData);

      const successMessage = `Successfully added ${newItems.length} item(s) to custom report`;
      notif.success(successMessage);

      handleCloseModal();
      clearSelection();
      
      // Return success for potential external use
      return { success: true, message: successMessage };
    } catch (err) {
      console.error('❌ Failed to add items:', err);
      notif.error('Failed to add items');
      return { success: false, message: 'Failed to add items' };
    } finally {
      setAdding(false);
    }
  };

  const getSelectedLinkEntity = () => {
    const { journalEntries = [], transDetails = {} } = context?.transactionsData || {};
    const firstJE = journalEntries[0];
    return getFieldValue(firstJE?.ixSubAcc1 || transDetails?.ixSubAcc1);
  };

  const getSelectedLinkEntityName = () => {
    const { transDetails = {} } = context?.transactionsData || {};
    return getFieldValue(transDetails?.Name) || '';
  };

  const getLinkEntityCaption = () => {
    // Try to get from transaction settings
    const transSubType = context?.meta?.transType?._rm?.subType?.['1']?.caption || '';
    if (transSubType) return transSubType;
    
    // Try to get from component settings
    if (employee_sub_type) return 'Link Entity';
    
    // Fallback to subType caption from data
    return data?.subType?.['1']?.caption || 'Entity';
  };

  const handleAssignItems = async () => {
    const linkId = getSelectedLinkEntity();
    const linkName = getSelectedLinkEntityName();
    const linkCaption = getLinkEntityCaption();

    if (!linkId) {
      return { success: false, message: `No ${linkCaption.toLowerCase()} selected` };
    }

    const currentData = normalizeToArray(persistedReportData || []);

    if (currentData.length === 0) {
      return { success: true, message: 'No items to assign' };
    }

    // Check if any item ID is undefined
    if (currentData.some(item => item.sub_id === undefined || item.sub_id === null)) {
      const errorMessage = 'Report data missing item ID. Please include sub_id in report SQL.';
      console.error(errorMessage);
      return { success: false, message: errorMessage };
    }

    setAdding(true);
    try {
      for (const item of currentData) {
        const payload = {
          values: {
            ixSubLink2: linkId
          }
        };

        const res = await req.put(
          endpoints.setup.sub.subDetails(item.sub_id),
          payload
        );

        if (!res.success) {
          throw new Error(`Failed to assign item ${item.sub_id}`);
        }
      }

      const successMessage = `Successfully assigned ${currentData.length} item(s) to ${linkName || linkCaption}`;
      notif.success(successMessage);

      // Mark that user has explicitly assigned items
      setHasExplicitlyAssigned(true);

      // Save all items into transaction KVS
      if (context?.transactionsData?.setKvs) {
        context.transactionsData.setKvs(prev => ({
          ...prev,
          [stableFieldKey]: {
            ...(prev?.[stableFieldKey] ?? {}),
            value: currentData
          }
        }));
      }

      return { success: true, message: successMessage, filteredData: currentData };
    } catch (err) {
      console.error('❌ Failed to assign items:', err);
      notif.error('Failed to assign items');
      return { success: false, message: 'Failed to assign items' };
    } finally {
      setAdding(false);
    }
  };

  const handleUnassignItems = async () => {
    const currentData = normalizeToArray(persistedReportData || []);

    if (currentData.length === 0) {
      return { success: true, message: 'No items to unassign' };
    }

    // Check if any item ID is undefined
    if (currentData.some(item => item.sub_id === undefined || item.sub_id === null)) {
      const errorMessage = 'Report data missing item ID. Please include sub_id in report SQL.';
      console.error(errorMessage);
      return { success: false, message: errorMessage };
    }

    setAdding(true);
    try {
      for (const item of currentData) {
        const payload = {
          values: {
            ixSubLink2: null // Clear the assignment
          }
        };

        const res = await req.put(
          endpoints.setup.sub.subDetails(item.sub_id),
          payload
        );

        if (!res.success) {
          throw new Error(`Failed to unassign item ${item.sub_id}`);
        }
      }

      const successMessage = `Successfully cleared assignment for ${currentData.length} item(s)`;
      notif.success(successMessage);

      // Mark that user has explicitly unassigned items
      setHasExplicitlyAssigned(true);

      // Save unassigned items into transaction KVS
      if (context?.transactionsData?.setKvs) {
        context.transactionsData.setKvs(prev => ({
          ...prev,
          [stableFieldKey]: {
            ...(prev?.[stableFieldKey] ?? {}),
            value: currentData
          }
        }));
      }

      return { success: true, message: successMessage, filteredData: currentData };
    } catch (err) {
      console.error('❌ Failed to unassign items:', err);
      notif.error('Failed to clear assignment');
      return { success: false, message: 'Failed to clear assignment' };
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!itemId) {
      notif.error('Invalid item ID');
      return;
    }

    setAdding(true);
    try {
      // Remove item from persisted data
      const currentData = normalizeToArray(persistedReportData || []);
      const updatedData = currentData.filter(item => item.sub_id !== itemId);

      // Mark that user has explicitly removed items
      setHasExplicitlyAssigned(true);

      // Update KVS with updated data
      if (context?.transactionsData?.setKvs) {
        context.transactionsData.setKvs(prev => ({
          ...prev,
          [stableFieldKey]: {
            ...(prev?.[stableFieldKey] ?? {}),
            value: updatedData
          }
        }));
      }

      // Update reportData to show updated data
      setReportData(updatedData);

      const successMessage = 'Successfully removed item from custom report';
      notif.success(successMessage);
    } catch (err) {
      console.error('❌ Failed to remove item:', err);
      notif.error('Failed to remove item');
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Always load report definition when ixRep is available
    if (ixRep) {
      loadReportDefinition().then(() => {
        if (!isMounted) return;
      });
    }

    return () => {
      isMounted = false;
    };
  }, [ixRep]);

  useEffect(() => {
    let isMounted = true;

    if (persistedReportData !== undefined && persistedReportData !== null) {
      const dataArray = normalizeToArray(persistedReportData);
      // Only set reportData if it's empty or in view mode
      // This prevents overwriting the data after assign/unassign operations
      if (isMounted && (reportData.length === 0 || isViewMode)) {
        setReportData(dataArray);
      }
      // Reset hasExplicitlyAssigned when loading persisted data
      // This allows auto-save to work again when data is loaded from saved state
      if (isMounted) {
        setHasExplicitlyAssigned(false);
      }
      if (isMounted && ixRep) {
        loadReportDefinition();
      }
      return;
    }

    if (isMounted) {
      setReportData([]);
    }

    return () => {
      isMounted = false;
    };
  }, [persistedReportData, ixRep, isViewMode, reportData.length]);

  // Expose globally for transaction save handler
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const globalContextKey = `__customReportSearchModalContext_${stableFieldKey}`;
      window[globalContextKey] = {
        handleAssignItems,
        handleUnassignItems,
        fieldKey: stableFieldKey,
        adding,
        persistedReportData,
        allow_unassign
      };
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        const globalContextKey = `__customReportSearchModalContext_${stableFieldKey}`;
        delete window[globalContextKey];
      }
    };
  }, [handleAssignItems, handleUnassignItems, stableFieldKey, adding, persistedReportData, allow_unassign]);

  return (
    <CustomReportSearchModalContext.Provider
      value={{
        loading,
        modalOpen,
        reportData,
        filteredReportData,
        modalFilteredData,
        reportDefinition,
        page,
        rowsPerPage,
        buttonLabel,
        disableActionButton,
        buttonVariant,
        buttonColor,
        handleOpenModal,
        handleCloseModal,
        handleSearch,
        searchTerm,
        handleChangePage,
        handleChangeRowsPerPage,
        selectedItems,
        toggleItemSelection,
        clearSelection,
        handleAddSelectedItems,
        adding,
        isViewMode,
        fieldKey: stableFieldKey,
        persistedReportData,
        caption,
        handleAssignItems,
        handleUnassignItems,
        allow_unassign,
        handleRemoveItem
      }}
    >
      {children}
    </CustomReportSearchModalContext.Provider>
  );
};